import { ActionIcon, Badge, Group, Stack, Text, Tooltip } from '@mantine/core'
import IonIcon from '@reacticons/ionicons'
import { MRT_ColumnDef, MRT_Row } from 'mantine-react-table'
import moment from 'moment'
import { formatNumberToCurrency } from 'app/utils/util-functions'
import {
  FacilityHistoryEntry,
  FacilityHistoryLoanDiff,
  FacilityHistoryTransactionType,
} from './facility-history-types'

const DEFAULT_LABELS: Record<FacilityHistoryTransactionType, string> = {
  FacilityCreation: 'Facility Creation',
  FacilityEdit: 'Facility Edit',
  FacilityAmendment: 'Facility Amendment',
  FacilityLenderAmendment: 'Facility Lender Amendment',
  FacilityUtilizedAmountChanged: 'Facility Utilized Amount Changed',
  LoanCreation: 'Loan Creation',
  LoanAmendment: 'Loan Amendment',
  LoanLenderAllocation: 'Loan Lender Allocation',
  LoanEdit: 'Loan Edit',
  LoanIncrease: 'Loan Increase',
  LoanMerge: 'Loan Merge',
  LoanConversion: 'Loan Conversion',
  LoanRollover: 'Loan Rollover',
  LoanPik: 'Loan PIK',
  LoanPrepayment: 'Loan Prepayment',
  LoanInterestPayment: 'Loan Interest Payment',
  LocBooked: 'LoC Booked',
  Trade: 'Trade',
  Assignment: 'Assignment',
  FacilityTermination: 'Facility Termination',
  ReprocessBlocker: 'Processing Blocker',
}

/**
 * The blocker is a marker on the timeline, not something anyone did: it moved
 * no money and had no author. Its figure and people columns render empty rather
 * than as "--", which would read as a value we failed to load.
 */
function isProcessingBlocker(entry: FacilityHistoryEntry): boolean {
  return entry.transactionType === 'ReprocessBlocker'
}

export function getTransactionLabel(entry: FacilityHistoryEntry): string {
  // The feed passes DAML transaction types through verbatim for anything this
  // union hasn't enumerated, so the map can miss — falling through to
  // `undefined` rendered those rows with a blank Transaction Type cell, which
  // read as "the row isn't there".
  return (
    entry.customLabel ??
    DEFAULT_LABELS[entry.transactionType] ??
    entry.transactionType
  )
}

function LevelCell({ row }: { row: MRT_Row<FacilityHistoryEntry> }) {
  const entry = row.original
  const isFacility = entry.level === 'Facility'
  return (
    <Stack spacing={2} align="flex-start">
      <Badge
        variant="light"
        radius="sm"
        size="lg"
        color={isFacility ? 'violet' : 'blue'}
        sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12 }}
      >
        {entry.level}
      </Badge>
      {entry.loanLabel ? (
        <Text sx={{ ...CELL_FONT, fontSize: 12 }} c="dimmed">
          {entry.loanLabel}
        </Text>
      ) : null}
    </Stack>
  )
}

function TransactionTypeCell({ row }: { row: MRT_Row<FacilityHistoryEntry> }) {
  const entry = row.original
  const label = getTransactionLabel(entry)
  const loanChanges = (entry.modifiedLoans ?? []).reduce(
    (sum: number, l: FacilityHistoryLoanDiff) => sum + l.modifiedFields.length,
    0,
  )
  const changes =
    entry.modifiedFields.length +
    loanChanges +
    (entry.modifiedLenders?.length ?? 0)


  return (
    <Group
      spacing={6}
      noWrap
      sx={{ cursor: 'pointer' }}
    >
      <Text
        sx={{
          ...CELL_FONT,
          fontWeight: 600,
          color: entry.state === 'Rejected' ? '#DC2626' : CELL_FONT.color,
        }}
      >
        {entry.state ? `${entry.state}: ${label}` : label}
      </Text>
      {!entry.revertReason && changes > 0 ? (
        <Badge
          size="lg"
          radius="sm"
          variant="light"
          color="indigo"
          sx={{ textTransform: 'none', fontWeight: 500, fontSize: 12 }}
        >
          {changes} {changes === 1 ? 'change' : 'changes'}
        </Badge>
      ) : null}
      {entry.openRevert ? (
        <Tooltip
          label={
            entry.openRevert?.reason
              ? `A reversal has been submitted for this transaction and is awaiting manager approval. With the message: "${entry.openRevert?.reason}"`
              : 'A reversal has been submitted for this transaction and is awaiting manager approval.'
          }
          withinPortal
        >
          <Badge
            size="lg"
            radius="sm"
            variant="light"
            color="yellow"
            sx={{ textTransform: 'none', fontWeight: 500, fontSize: 12 }}
          >
            Reverse submitted
          </Badge>
        </Tooltip>
      ) : null}
      {entry.revertReason ? (
        <Tooltip label={`Reversed: ${entry.revertReason}`} withinPortal>
          <Badge
            size="lg"
            radius="sm"
            variant="light"
            color="red"
            sx={{ textTransform: 'none', fontWeight: 500, fontSize: 12 }}
          >
            Reversed
          </Badge>
        </Tooltip>
      ) : null}
    </Group>
  )
}

// Only a fallback for entries that carry no currency of their own; the detail
// panel uses the same default, so the two never disagree.
const DEFAULT_CURRENCY = 'USD'

// Facility figures come through as raw decimal strings, denominated in the
// entry's currency — hardcoding USD misprints the symbol for any facility that
// isn't USD-based.
function formatAmount(value: string | undefined, currency?: string): string {
  if (value === null || value === undefined || value === '') return '--'
  const n = Number(value)
  if (!Number.isFinite(n)) return '--'
  return formatNumberToCurrency(n, currency ?? DEFAULT_CURRENCY, 2, 2)
}

/**
 * The grid's date format. Exported because the detail panel quotes the same
 * dates back to the operator in prose and must not drift into a second format.
 */
export function formatHistoryDate(value: string | null): string {
  if (!value) return '--'
  const m = moment(value)
  return m.isValid() ? m.format('DD-MMM-YYYY') : '--'
}

function formatDateTime(value: string): string {
  const m = moment(value)
  return m.isValid() ? m.format('DD-MMM-YYYY hh:mm A') : '--'
}

/**
 * Per-column styling for the History grid. Applied here rather than on
 * MainTable, which every table in the app shares — a font change there would
 * resize the whole product.
 */
// Type scale copied from the facility-loans grid (loans-page-coumnDefs.tsx),
// which is the reference for expandable tables in this app.
const CELL_FONT = {
  fontFamily: 'Plus Jakarta Sans',
  fontSize: 14,
  lineHeight: '19px',
  color: '#413E3E',
} as const

// Headers sit a step above the 14px cell values — deliberately larger than the
// loans grid, which runs headers and cells at the same size.
/**
 * Headers are rendered as a component rather than styled through the cell's
 * `sx`, which is how the loans grid does it (TableHead in
 * loans-page-coumnDefs.tsx). Styling the `th` alone left MRT's own header
 * element winning on font-size, so the headers stayed small.
 */
function HeadText({ children }: { children: React.ReactNode }) {
  return (
    <Text
      sx={{
        ...CELL_FONT,
        fontSize: 16,
        lineHeight: '22px',
        fontWeight: 700,
      }}
    >
      {children}
    </Text>
  )
}

const HEAD_CELL_PROPS = {
  sx: {
    ...CELL_FONT,
    fontSize: 16,
    lineHeight: '22px',
    fontWeight: 700,
    whiteSpace: 'nowrap' as const,
  },
}

const BODY_CELL_PROPS = {
  sx: {
    // 19px line box + 13px top and bottom holds the row at ~44px. `nowrap`
    // keeps every column on a single line so heights stay uniform.
    padding: '13px 16px',
    whiteSpace: 'nowrap' as const,
  },
}

interface ColumnOptions {
  onRevert: (entry: FacilityHistoryEntry) => void
  onApproveRevert: (entry: FacilityHistoryEntry) => void
  onRejectRevert: (entry: FacilityHistoryEntry) => void
  /**
   * The reversal whose approve/reject request is in flight. Both controls for
   * that reversal are disabled until it settles, so a second click can't send a
   * conflicting decision for the same contract.
   */
  pendingRevertId: string | null
}

function ActionsCell({
  row,
  onRevert,
  onApproveRevert,
  onRejectRevert,
  pendingRevertId,
}: {
  row: MRT_Row<FacilityHistoryEntry>
} & ColumnOptions) {
  const entry = row.original
  const isDeciding =
    !!entry.openRevert && entry.openRevert.id === pendingRevertId

  // A submitted reverse is decided here — the same approve/reject pair the
  // servicing view offers, so a manager doesn't have to leave the history tab.
  if (entry.openRevert?.status === 'Submitted') {
    return (
      <Group spacing={2} noWrap>
        <Tooltip label="Approve reversal" withinPortal>
          <ActionIcon
            variant="subtle"
            color="green"
            size="24px"
            loading={isDeciding}
            disabled={isDeciding}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              onApproveRevert(entry)
            }}
            aria-label="Approve reversal"
          >
            <IonIcon name="checkmark-circle-outline" style={{ width: 20, height: 10, paddingBottom: 8 }} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Reject reversal" withinPortal>
          <ActionIcon
            variant="subtle"
            color="red"
            size="xl"
            disabled={isDeciding}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              onRejectRevert(entry)
            }}
            aria-label="Reject reversal"
          >
            <IonIcon name="close-circle-outline" style={{ width: 20, height: 10, paddingBottom: 8 }} />
          </ActionIcon>
        </Tooltip>
      </Group>
    )
  }

  // Blocked by the processing blocker: show the control disabled with the
  // reason, rather than nothing at all. The absence of a button says "not
  // supported here"; a greyed-out one with the explanation says why this
  // particular action is out of reach.
  if (!entry.canRevert && entry.revertBlockedBefore) {
    return (
      <Tooltip
        label={`Can't revert — this transaction is effective on or before ${formatHistoryDate(
          entry.revertBlockedBefore,
        )}. Post an adjusting transaction instead.`}
        withinPortal
        multiline
        w={320}
      >
        <span>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="xl"
            disabled
            aria-label="Reverse transaction unavailable"
          >
            <IonIcon name="arrow-undo-outline" style={{ width: 20, height: 10, paddingBottom: 8 }} />
          </ActionIcon>
        </span>
      </Tooltip>
    )
  }

  // A drafted reverse is still awaiting submission, which happens from the
  // servicing view; nothing to act on here. Nor when the action can't be
  // reversed at all — an always-visible disabled icon was just noise.
  if (!entry.canRevert) return null

  return (
    <Tooltip label="Reverse transaction" withinPortal>
      <ActionIcon
        variant="subtle"
        color="blue"
        size="xl"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
          onRevert(entry)
        }}
        aria-label="Reverse transaction"
      >
        <IonIcon name="arrow-undo-outline" style={{ width: 20, height: 10, paddingBottom: 8 }} />
      </ActionIcon>
    </Tooltip>
  )
}

export function getFacilityHistoryColumns({
  onRevert,
  onApproveRevert,
  onRejectRevert,
  pendingRevertId,
}: ColumnOptions): MRT_ColumnDef<FacilityHistoryEntry>[] {
  const columns: MRT_ColumnDef<FacilityHistoryEntry>[] = [
    {
      id: 'level',
      header: 'Level',
      mantineTableHeadCellProps: HEAD_CELL_PROPS,
      mantineTableBodyCellProps: BODY_CELL_PROPS,
      accessorFn: (row) => row.level,
      size: 110,
      enableSorting: false,
      Cell: ({ row }) => <LevelCell row={row} />,
    },
    {
      id: 'transactionType',
      header: 'Transaction Type',
      mantineTableHeadCellProps: HEAD_CELL_PROPS,
      mantineTableBodyCellProps: BODY_CELL_PROPS,
      accessorFn: (row) => getTransactionLabel(row),
      size: 320,
      Cell: ({ row }) => <TransactionTypeCell row={row} />,
    },
    {
      id: 'effectiveDate',
      header: 'Effective Date',
      mantineTableHeadCellProps: HEAD_CELL_PROPS,
      mantineTableBodyCellProps: BODY_CELL_PROPS,
      accessorFn: (row) => row.effectiveDate ?? '',
      sortingFn: 'datetime',
      filterVariant: 'date',
      size: 140,
      Cell: ({ row }) => (
        <Text sx={{ ...CELL_FONT, fontWeight: 600 }}>{formatHistoryDate(row.original.effectiveDate)}</Text>
      ),
    },
    {
      id: 'facilityAmount',
      header: 'Amount',
      mantineTableHeadCellProps: HEAD_CELL_PROPS,
      mantineTableBodyCellProps: BODY_CELL_PROPS,
      // Numeric accessor: sorting the raw decimal string would order
      // lexicographically ("1000" before "900").
      accessorFn: (row) =>
        row.facilityAmount != null ? Number(row.facilityAmount) : null,
      sortingFn: 'basic',
      size: 150,
      Cell: ({ row }) => (
        <Text sx={{ ...CELL_FONT, fontWeight: 600 }}>
          {isProcessingBlocker(row.original)
            ? ''
            : formatAmount(row.original.facilityAmount, row.original.currency)}
        </Text>
      ),
    },
    {
      id: 'facilityUtilizedAmount',
      header: 'Utilized Amount',
      mantineTableHeadCellProps: HEAD_CELL_PROPS,
      mantineTableBodyCellProps: BODY_CELL_PROPS,
      accessorFn: (row) =>
        row.facilityUtilizedAmount != null
          ? Number(row.facilityUtilizedAmount)
          : null,
      sortingFn: 'basic',
      size: 170,
      Cell: ({ row }) => (
        <Text sx={{ ...CELL_FONT, fontWeight: 600 }}>
          {isProcessingBlocker(row.original)
            ? ''
            : formatAmount(
                row.original.facilityUtilizedAmount,
                row.original.currency,
              )}
        </Text>
      ),
    },
    {
      id: 'user',
      header: 'User',
      mantineTableHeadCellProps: HEAD_CELL_PROPS,
      mantineTableBodyCellProps: BODY_CELL_PROPS,
      accessorFn: (row) => row.user ?? '',
      size: 140,
      Cell: ({ row }) => (
        <Text sx={{ ...CELL_FONT, fontWeight: 600 }}>
          {isProcessingBlocker(row.original) ? '' : (row.original.user ?? '--')}
        </Text>
      ),
    },
    {
      id: 'approver',
      header: 'Approver',
      mantineTableHeadCellProps: HEAD_CELL_PROPS,
      mantineTableBodyCellProps: BODY_CELL_PROPS,
      accessorFn: (row) => row.approver ?? '',
      size: 140,
      Cell: ({ row }) => (
        <Text sx={{ ...CELL_FONT, fontWeight: 600 }}>
          {isProcessingBlocker(row.original)
            ? ''
            : (row.original.approver ?? '--')}
        </Text>
      ),
    },
    {
      id: 'processedAt',
      header: 'Processed Time',
      mantineTableHeadCellProps: HEAD_CELL_PROPS,
      mantineTableBodyCellProps: BODY_CELL_PROPS,
      accessorFn: (row) => row.processedAt,
      sortingFn: 'datetime',
      filterVariant: 'date',
      size: 200,
      Cell: ({ row }) => (
        <Text sx={{ ...CELL_FONT, fontWeight: 600 }}>{formatDateTime(row.original.processedAt)}</Text>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      mantineTableHeadCellProps: HEAD_CELL_PROPS,
      mantineTableBodyCellProps: BODY_CELL_PROPS,
      enableSorting: false,
      enableColumnFilter: false,
      // Wide enough for the approve/reject pair at the larger icon size.
      size: 140,
      Cell: ({ row }) => (
        <ActionsCell
          row={row}
          onRevert={onRevert}
          onApproveRevert={onApproveRevert}
          onRejectRevert={onRejectRevert}
          pendingRevertId={pendingRevertId}
        />
      ),
    },
  ]

  // Every header goes through HeadText so none can drift back to MRT's default.
  return columns.map((column) => ({
    ...column,
    Header: <HeadText>{column.header as string}</HeadText>,
  }))
}

